import type {
  CandidateInfo,
  CheckWebLinkResponse,
  InterviewAccessPurpose,
} from 'api/generated/types';

import { isLiveKitCallSoftware } from '../services/callClientLoader';
import type { InFlightInterviewMarker } from '../session/inFlightInterviewMarker';
import type { WebCallInitialAction } from '../session/webCallSessionTypes';
import type { LinkResolutionCandidateInfo } from './linkResolutionTypes';

// check_web_link's candidate_info leaves email/phone/resume nullable; both
// branches that seed page state from it need the same empty-string/null
// fallbacks applied.
export function toLinkResolutionCandidateInfo(
  candidateInfo: CandidateInfo
): LinkResolutionCandidateInfo {
  return {
    full_name: candidateInfo.full_name,
    email: candidateInfo.email ?? '',
    phone_number: candidateInfo.phone_number ?? '',
    existing_resume_name: candidateInfo.existing_resume_name ?? null,
  };
}
type LinkIntakeDecision =
  | { kind: 'expired' }
  | { kind: 'shared_form' }
  | { kind: 'individual_form'; candidateInfo: CandidateInfo }
  | { kind: 'validated'; candidateInfo: CandidateInfo | null };

/** Decide the form/validation projection after recovery, resume and scheduling. */
export function linkIntakeDecision(
  response: CheckWebLinkResponse,
  isExpired: boolean,
  isCandidateActionLink: boolean,
  intakeCompleted: boolean
): LinkIntakeDecision {
  if (isExpired) return { kind: 'expired' };
  if (response.is_shared_link) return { kind: 'shared_form' };
  const candidateInfo = response.candidate_info;
  if (candidateInfo && (!intakeCompleted || isCandidateActionLink)) {
    return { kind: 'individual_form', candidateInfo };
  }
  return { kind: 'validated', candidateInfo };
}

// Where a resolved link sends the candidate, in applyResolvedInterviewLink's
// order: deleted-link recovery, then a live interview's resume, then scheduling
// before the interview, then intake. Pure, so the interview app's boot can tell
// ahead of the page whether the scheduling page comes next.

/** `?action=accommodation` and `?action=opt-out` open the intake form first. */
export const isCandidateActionLink = (
  initialAction: WebCallInitialAction | null
): boolean => initialAction === 'accommodation' || initialAction === 'opt-out';

/**
 * A deleted link that shows its recovery screen and nothing else. A
 * dispositioned application must not offer deleted-link recovery: its
 * closed-role screen is the expired phase with dispositionedCandidate set.
 */
export const isRecoverableDeletedLink = (
  response: CheckWebLinkResponse
): boolean =>
  response.link_state === 'deleted' && !response.dispositioned_candidate;

/** A link that no longer admits an interview. */
export const isExpiredLink = (response: CheckWebLinkResponse): boolean =>
  response.expired ||
  (response.dispositioned_candidate && response.link_state === 'deleted');

/** What besides check_web_link decides where a resolved link goes. */
export interface LinkRouteContext {
  /** The purpose of the interview session the link opened. */
  sessionPurpose: InterviewAccessPurpose | undefined;
  isCandidateActionLink: boolean;
  /** This tab's live shared-link interview, read only when one could resume. */
  inFlightMarker: () => InFlightInterviewMarker | null;
}

export type LiveInterviewResume =
  | { kind: 'individual'; callId: string }
  | { kind: 'shared'; marker: InFlightInterviewMarker };

/**
 * The live LiveKit interview a resolved link reconnects to (a page refresh or
 * a re-opened link mid-interview), skipping intake. Individual links are
 * detected server-side; shared links rely on this tab's marker because the
 * server cannot identify a shared-link visitor.
 */
export function liveInterviewResume(
  response: CheckWebLinkResponse,
  isExpired: boolean,
  context: LinkRouteContext
): LiveInterviewResume | null {
  if (
    isExpired ||
    context.isCandidateActionLink ||
    !isLiveKitCallSoftware(response.call_software)
  ) {
    return null;
  }
  if (!response.is_shared_link) {
    return response.resume_in_flight_available && response.call_id
      ? { kind: 'individual', callId: response.call_id }
      : null;
  }
  const marker = context.inFlightMarker();
  return marker && marker.campaignId === response.campaign_id
    ? { kind: 'shared', marker }
    : null;
}

/**
 * Whether a job that books its interviews sends the candidate to book one
 * before the interview. Action links come first, so candidates can opt out or
 * request accommodations without booking a slot (the /schedule redirect would
 * also drop the action query param).
 */
export const schedulesBeforeInterview = (
  response: CheckWebLinkResponse,
  isExpired: boolean,
  context: LinkRouteContext
): boolean =>
  response.enable_interview_scheduling &&
  !response.has_scheduled_interview &&
  !isExpired &&
  context.sessionPurpose === 'interview' &&
  !response.is_test_call &&
  !context.isCandidateActionLink;

/** Whether the landing page sends a resolved link to the scheduling page. */
export function opensSchedulingPage(
  response: CheckWebLinkResponse,
  context: LinkRouteContext
): boolean {
  if (isRecoverableDeletedLink(response)) return false;
  const isExpired = isExpiredLink(response);
  return (
    !liveInterviewResume(response, isExpired, context) &&
    schedulesBeforeInterview(response, isExpired, context)
  );
}
